import React from 'react';

/**
 * Phase F — active-plan visibility (sidebar). Teammate surface only.
 *
 * The owner-facing surface moved to a standalone <PlanCard> component
 * (rendered above the account card in the sidebar) — that change
 * cleaned up the previous arrangement where billing state and account
 * identity shared a single bordered card with the plan as a small
 * crammed chip. This component now only handles the teammate case.
 *
 * **Teammate**: a tiny tier-colored dot, embedded inside account-meta
 * next to the name/email. Per decision #8 we do NOT name the plan, do
 * NOT add a tooltip, and never link anywhere. The dot is presence-only
 * signal that the workspace is on a paid plan — teammates aren't shown
 * billing details.
 *
 * Hides silently when:
 *   - viewer is the owner (PlanCard handles them)
 *   - no active owner subscription
 *   - tier we don't have a color for (e.g. Enterprise, which can't end
 *     up in planTier per decision #4)
 */

type Tier = 'core' | 'premium' | 'enterprise';

const TIER_COLOR: Record<Tier, string> = {
  core: '#0f7a7d', // brand teal
  premium: '#4f46e5', // indigo
  enterprise: '#b45309', // gold
};

interface PlanIndicatorProps {
  user: any;
}

const PlanIndicator: React.FC<PlanIndicatorProps> = ({ user }) => {
  if (!user) return null;

  // Owners are served by <PlanCard> above the account card.
  if (user.role === 'owner') return null;

  // Teammate surface — the dot only.
  const ownerSub = user.ownerSubscription;
  const tier = ownerSub?.planTier as Tier | undefined;
  if (!ownerSub?.active || !tier || !TIER_COLOR[tier]) return null;

  const color = TIER_COLOR[tier];
  // Decision #8 — no aria-label naming the tier, no title, no copy.
  // The dot signals "this workspace is on a paid plan" and nothing more.
  return (
    <div style={{ display: 'inline-block', marginTop: 6 }}>
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: color,
          boxShadow: `0 0 0 2px ${hexWithAlpha(color, 0.18)}`,
        }}
      />
    </div>
  );
};

/** Take a "#rrggbb" hex and return an rgba() string with the requested
 *  alpha. Used so the chip background can fade the brand color without
 *  hardcoding rgba literals per tier. */
function hexWithAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const r = parseInt(m[1].slice(0, 2), 16);
  const g = parseInt(m[1].slice(2, 4), 16);
  const b = parseInt(m[1].slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default PlanIndicator;
